import React, { useMemo, useState } from "react";
import { Users } from "lucide-react";
import { List, Task } from "../../../types";
import { useAuth } from "../../../contexts/AuthContext";
import { useListMembers } from "../../../hooks/queries/useListMembers";
import { useLeaveList } from "../../../hooks/mutations/useLeaveList";
import {
  acceptedMemberListIds,
  EMPTY_LIST_MEMBERS,
  myPendingInvitations,
} from "../../../lib/listSharing";
import SharedListMark from "../../ui/SharedListMark";
import EmptyState from "../../misc/EmptyState";
import PendingInvitation from "../../misc/PendingInvitation";

interface SharedListsSettingsProps {
  lists: List[];
  tasks: Task[];
}

/**
 * "Lists shared with me" (build plan Session F, item 3) — every List this
 * person has *accepted* Membership on, plus unanswered Invitations so they
 * can accept or decline here as well as from the invitation toast. Mirrors iOS's
 * SharedListsView for the accepted rows, plus the Leave button iOS
 * deliberately leaves out (its self-service is web-only for V1 —
 * docs/shared-lists-deferred-edges.md).
 */
const SharedListsSettings: React.FC<SharedListsSettingsProps> = ({ lists, tasks }) => {
  const { user } = useAuth();
  const { data: memberRows } = useListMembers();
  const members = memberRows ?? EMPTY_LIST_MEMBERS;
  const leaveList = useLeaveList();
  // Leaving is not undoable — the List goes, and only a fresh Invitation
  // brings it back — so the row asks first, the same way "Stop sharing"
  // does in the Owner's panel.
  const [confirmingLeaveId, setConfirmingLeaveId] = useState<string | null>(null);

  const pendingInvites = useMemo(
    () => (user ? myPendingInvitations(members, user.id) : []),
    [members, user],
  );

  const sharedLists = useMemo(() => {
    if (!user) return [];
    const accepted = acceptedMemberListIds(members, user.id);
    return lists.filter((l) => accepted.has(l.id));
  }, [lists, members, user]);

  const isEmpty = pendingInvites.length === 0 && sharedLists.length === 0;

  return (
    <div>
      <h3 className="text-lg font-semibold text-warm-text-primary mb-3">Shared with me</h3>

      {isEmpty ? (
        <EmptyState
          icon={Users}
          title="Nothing shared with you yet"
          description="Lists someone shares with you, and invitations waiting for an answer, will show up here."
        />
      ) : (
        <div className="space-y-6">
          {pendingInvites.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
                Pending invitations
              </h4>
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <PendingInvitation key={invite.list_id} invite={invite} lists={lists} />
                ))}
              </div>
            </div>
          )}

          {sharedLists.length > 0 && (
            <div>
              {pendingInvites.length > 0 && (
                <h4 className="text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
                  Lists you&apos;ve joined
                </h4>
              )}
              <div className="rounded-xl border border-warm-border bg-warm-bg-elevated divide-y divide-warm-border overflow-hidden">
                {sharedLists.map((list) => {
                  const taskCount = tasks.filter((t) => t.list_id === list.id).length;
                  return (
                    <div key={list.id} className="px-3 py-2.5 flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className="text-base leading-none">{list.emoji}</span>
                        <span className="text-sm text-warm-text-primary font-medium truncate">
                          {list.name}
                        </span>
                        <SharedListMark size={12} />
                        <span className="text-xs text-warm-text-tertiary">
                          {taskCount} {taskCount === 1 ? "task" : "tasks"}
                        </span>
                      </span>
                      {confirmingLeaveId === list.id ? (
                        <span className="flex-shrink-0 flex items-center gap-1">
                          <span className="text-xs text-warm-text-secondary mr-1">
                            Leave this list?
                          </span>
                          <button
                            onClick={() => setConfirmingLeaveId(null)}
                            className="px-2.5 py-1 rounded-md text-xs text-warm-text-secondary hover:text-warm-text-primary transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              leaveList.mutate(list.id);
                              setConfirmingLeaveId(null);
                            }}
                            aria-label={`Confirm leaving ${list.name}`}
                            className="px-2.5 py-1 rounded-md text-xs bg-red-600 hover:bg-red-700 text-white transition-colors"
                          >
                            Leave
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setConfirmingLeaveId(list.id)}
                          aria-label={`Leave ${list.name}`}
                          className="flex-shrink-0 px-2.5 py-1 rounded-md text-xs text-warm-text-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        >
                          Leave
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SharedListsSettings;
